/**
 * Der Haken „Mensch im Unternehmen (bAV/bKV)" — Ableitung aus den gewählten Rollen.
 *
 * Fachlicher Rahmen (PO 15.08.2026, Plan §4.2):
 *  • Der Haken ist KEINE Rolle und keine Beziehungsart, sondern ein Feld an der
 *    Verbindung. Sein Zweck ist der Mitarbeiter OHNE jede Verantwortlichkeit —
 *    ohne ihn gäbe es für bAV/bKV nichts zu erfassen.
 *  • Vorschlagswert: `EXISTS(Rolle mit istMenschImUnternehmen)`, also intern
 *    leitende und vertretende Rollen (Geschäftsführer, Prokurist, Vorstand).
 *    Reine Gesellschafter und Aufsichtsorgane tragen ihn nicht.
 *  • `NOT isExtern` ist Teil der Regel, nicht Kosmetik: 73 der 82 WEG-Verwalter
 *    stehen als extern in den Daten (Plan §4.2) — ohne diesen Teil entstünden
 *    dort 73 falsche Mitarbeiter in der bAV-Grundlage.
 *  • Setzt der Anwender gegen die Ableitung, merkt sich das ein zweites Flag und
 *    die Automatik fasst den Haken nicht mehr an.
 *
 * Bewusst ohne React/DOM, damit die Regel per Vitest prüfbar ist — wie
 * verantwortlichkeitKategorien.ts.
 */

import { OPT_GES, OPT_GF } from "./verantwortlichkeitKategorien";
import type { VerantRole } from "./VerantwortlichkeitInput";

/**
 * Options-Wert einer gewählten Rolle — der Schlüssel, unter dem sie in der
 * Rollen-/Matrix-Tabelle steht. Geschäftsführer und Gesellschafter laufen über
 * Pseudo-Werte, weil an ihnen im Speicherpfad eigene Beziehungstypen hängen.
 */
export function rollenSchluessel(r: VerantRole): string {
  if (r.kind === "gf") return OPT_GF;
  if (r.kind === "gesellschafter") return OPT_GES;
  return r.verantwortlichkeit;
}

export type HakenEingabe = {
  roles: VerantRole[];
  /**
   * Kann diese Verbindung überhaupt einen Menschen im Unternehmen meinen?
   * Falsch bei „extern" (ein beauftragter Steuerberater arbeitet nicht dort) und
   * wenn der Gegenkontakt selbst eine Firma ist (eine Komplementär-GmbH ist kein
   * Mensch) — dieselbe Grenze zieht das Backend beim Status „Mensch fürs
   * Unternehmen".
   */
  hakenMoeglich: boolean;
  istMenschImUnternehmen?: boolean;
  istMenschImUnternehmenManuell?: boolean;
};

/**
 * Der abgeleitete Vorschlag allein — ohne die manuelle Übersteuerung. Wird
 * gebraucht, um zu erkennen, ob eine Anwender-Eingabe überhaupt „gegen" die
 * Ableitung geht.
 */
export function abgeleiteterHaken(
  e: Pick<HakenEingabe, "roles" | "hakenMoeglich">,
  hakenJeRolle: ReadonlyMap<string, boolean>,
): boolean {
  if (!e.hakenMoeglich) return false;
  // Freitext („Sonstiges") steht in keinem Katalog und liefert deshalb kein
  // `true` — welche Rolle gemeint ist, weiß niemand.
  return e.roles.some((r) => hakenJeRolle.get(rollenSchluessel(r)) === true);
}

/** Der wirksame Haken: manuelle Setzung schlägt die Ableitung. */
export function menschImUnternehmenWert(
  e: HakenEingabe,
  hakenJeRolle: ReadonlyMap<string, boolean>,
): boolean {
  if (!e.hakenMoeglich) return false;
  if (e.istMenschImUnternehmenManuell) return !!e.istMenschImUnternehmen;
  return abgeleiteterHaken(e, hakenJeRolle);
}

/**
 * Nachschlagetabelle „Rolle → trägt den Haken".
 *
 * Die Matrix-Zeile gewinnt gegen den Katalog: Dieselbe Rolle liegt je Rechtsform
 * anders — der Vereinsvorstand ist nach § 27 Abs. 3 S. 2 BGB ehrenamtlich, der
 * Vorstand einer AG angestellt.
 *
 * ⚠️ Der Katalog führt 10 Bezeichnungen doppelt (intern + extern). Ohne den
 * art-Filter überschriebe die externe Zeile die interne und der Haken kippte.
 */
export function baueHakenJeRolle(
  katalog: Array<{ bezeichnung: string; art: "intern" | "extern"; istMenschImUnternehmen?: boolean }>,
  matrix: Array<{ schluessel: string | null; bezeichnung: string; istMenschImUnternehmen: boolean }>,
  optionsWert: (m: { schluessel: string | null; bezeichnung: string }) => string,
): Map<string, boolean> {
  const m = new Map<string, boolean>();
  for (const i of katalog) {
    if (i.art === "extern") continue;
    m.set(i.bezeichnung, !!i.istMenschImUnternehmen);
  }
  for (const zeile of matrix) {
    m.set(optionsWert(zeile), !!zeile.istMenschImUnternehmen);
  }
  // Rückfall ohne Rechtsform: die Auswahl kennt dann nur diese beiden Rollen
  // (verantwortlichkeitKategorien.baueRollenOptionen). Ein Geschäftsführer
  // arbeitet im Unternehmen, ein reiner Gesellschafter nicht.
  if (!m.has(OPT_GF)) m.set(OPT_GF, true);
  if (!m.has(OPT_GES)) m.set(OPT_GES, false);
  return m;
}

// ── Vorschlag bei einer NEUEN Verantwortung (Paket A8, User 02.10.2026) ─────
//
// Wer eine neue Verantwortung anlegt, bekommt den Haken für alle internen
// Rollen vorbelegt — AUSSER für Gesellschafter (und andere reine Beteiligte),
// Aufsichtsrat/Beirat/Kuratorium und Bevollmächtigte. Extern und eine Firma als
// Gegenüber tragen ihn nie (`hakenMoeglich`). Arbeitgeber und Berufsstatus
// setzt das bewusst NICHT — das passiert beim Zuweisen des Arbeitgebers.
//
// Bestehende Einträge behalten die Ableitung aus Katalog/Matrix (oben). Damit
// ein so vorbelegter Haken beim späteren Öffnen nicht wieder kippt, kennzeichnet
// die Eingabe ihn als manuell, sobald er von dieser Ableitung abweicht.

/** Was die Eingabe über eine gewählte Rolle weiß (aus der Optionsliste). */
export type RollenInfo = { kategorie?: string; label?: string };

const OHNE_VORSCHLAG_NAME = /aufsicht|beirat|kuratori/i;
const BEVOLLMAECHTIGTER = /^bevollm(ä|ae)chtigte[rn]?$/i;

/** Trägt diese Rolle bei einer neuen Verantwortung KEINEN vorbelegten Haken? */
export function rolleOhneHakenVorschlag(r: VerantRole, info?: RollenInfo): boolean {
  if (r.kind === "gf") return false;
  if (r.kind === "gesellschafter") return true;
  if (info?.kategorie === "GESELLSCHAFTER" || info?.kategorie === "AUFSICHT") return true;
  const name = (info?.label ?? r.verantwortlichkeit).trim();
  return OHNE_VORSCHLAG_NAME.test(name) || BEVOLLMAECHTIGTER.test(name);
}

/** Vorbelegter Haken einer NEUEN Verantwortung. */
export function hakenVorschlagNeu(
  roles: VerantRole[],
  hakenMoeglich: boolean,
  infoVon: (schluessel: string) => RollenInfo | undefined,
): boolean {
  if (!hakenMoeglich) return false;
  return roles.some((r) => !rolleOhneHakenVorschlag(r, infoVon(rollenSchluessel(r))));
}
